import React, { useMemo, useRef, useLayoutEffect } from 'react';
import { animated } from 'react-spring';
import { useSliderState } from './Slider';
import SlideItem from './lib/SlideItem';

import {
  DIRECTION_X,
  SNAP_START,
  offsetProperties,
  translateProperties,
} from './lib/constants';
import { useVirtualItems } from './lib/use-virtual-items';
import { getSnapOffset } from './lib/helpers';
import { useTrackMetrics } from './lib/use-track-metrics';
import { useVirtualIndex } from './lib/use-virtual-index';
import { useAnimatedTrack } from './lib/use-animated-track';
import { useSize } from './lib/use-size';

export const SlideTrack = ({
  infinite = false,
  snap = SNAP_START,
  direction = DIRECTION_X,
  dragTreshold = 300,
  renderItem,
  className,
  height = 'style',
}) => {
  const offsetProp = offsetProperties[direction];
  const {
    index,
    items,
    increment,
    actions,
    inTransition,
    isLast,
    isFirst,
  } = useSliderState();
  const inTransitionRef = useRef(inTransition);
  inTransitionRef.current = inTransition;
  const [value, bind, transitionToPosition] = useAnimatedTrack(
    direction,
    actions,
    dragTreshold,
    inTransitionRef,
    infinite || !isLast,
    infinite || !isFirst,
  );
  const trackRef = useRef();
  const {
    trackSize,
    contentSize,
    itemSizes,
    offsets,
    minHeight,
    maxHeight,
  } = useTrackMetrics(trackRef, items, offsetProp);
  const virtualIndex = useVirtualIndex(items, index, increment, infinite);
  const virtualItems = useVirtualItems(
    items,
    contentSize,
    trackSize,
    offsets,
    infinite,
    offsetProp,
  );

  const virtualItem = useMemo(
    () => virtualItems.find(item => item.index === virtualIndex),
    [virtualIndex, virtualItems],
  );
  const realItem = useMemo(
    () => virtualItems.find(item => item.index === index),
    [index, virtualItems],
  );

  const virtualPosition = useMemo(() => {
    const itemSize = itemSizes[virtualItem.realIndex];
    if (itemSize === undefined || virtualItem.offset === undefined) return 0;
    return getSnapOffset(
      snap,
      offsetProp,
      virtualItem.offset,
      itemSize,
      trackSize,
    );
  }, [virtualItem, itemSizes, trackSize, offsetProp, snap]);
  const realPosition = useMemo(() => {
    const itemSize = itemSizes[realItem.realIndex];
    if (itemSize === undefined || realItem.offset === undefined) return 0;
    return getSnapOffset(
      snap,
      offsetProp,
      realItem.offset,
      itemSize,
      trackSize,
    );
  }, [realItem, itemSizes, trackSize, offsetProp, snap]);

  const lastVirtualIndex = useRef(null);
  useLayoutEffect(() => {
    const immediate =
      virtualIndex === null || virtualIndex === lastVirtualIndex.current;
    lastVirtualIndex.current = virtualIndex;
    transitionToPosition(virtualPosition, realPosition, immediate);
  }, [virtualIndex, virtualPosition, realPosition, transitionToPosition]);

  const heightStyle = useMemo(() => {
    switch (height) {
      case 'min':
        return { height: minHeight };
      case 'max':
        return { height: maxHeight };
      case 'item':
        return { height: itemSizes[realItem.realIndex].height };
      default:
        return {};
    }
  }, [height, minHeight, maxHeight, itemSizes, realItem]);

  return (
    <animated.div
      ref={trackRef}
      {...(virtualItems.length > 1 && bind())}
      style={{
        position: 'relative',
        visibility: isNaN(virtualPosition) ? 'hidden' : 'visible',
        userSelect: 'none',
        ...heightStyle,
        transform: value.interpolate(
          px => `${translateProperties[direction]}(${px}px)`,
        ),
      }}
      className={className}
      {...(inTransition && {
        onClick: e => e.preventDefault(),
      })}
    >
      {virtualItems.map(item => (
        <SlideItem
          key={item.index}
          isClone={item.isClone}
          item={item.item}
          index={item.index}
          realIndex={item.realIndex}
          isCurrent={item.realIndex === index}
          renderItem={renderItem}
          direction={direction}
          offset={item.offset}
        />
      ))}
    </animated.div>
  );
};

export default SlideTrack;
